import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useState } from 'react';
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import { CopyIcon } from 'lucide-react';
import { Form } from '@/components/ui/form.tsx';
import { ExpandedState, Row, RowData, RowSelectionState } from '@tanstack/react-table';
import {
  collectDeviceSns,
  createDefaultDevice,
  createDeviceSchemaWithSnCheck,
  TDevice,
  TTreeDevice,
} from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';
import {
  appendChildAtPath,
  buildSuffixes,
  cloneSubDevicesWithNewIdentifiers,
  flattenDevicesWithPath,
  getDeviceAtPath,
  getParentPath,
  insertSiblingAfterPath,
  parseRowPath,
  shiftExpandedKeys,
} from '@/features/devices/components/device-tree-table/device-tree-table.utils.ts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx';
import { DeviceEditDetailsTabForm } from '@/features/devices/components/device-tree-table/device-edit-details-tab-form.tsx';
import { DeviceEditMoveTabForm } from '@/features/devices/components/device-tree-table/device-edit-move-tab-form.tsx';

const ROOT_PATH = '__root__';

export const DeviceTableToolbarDuplicateButton = ({
  data,
  deviceInfo,
  hasSelectedRow,
  getSelectedTableRow,
  getSelectedDevice,
  setData,
  setExpandedRows,
  setSelectedRows,
}: {
  data: TTreeDevice[];
  deviceInfo: {
    plantId: string;
    timezone: string;
  };
  hasSelectedRow: boolean;
  getSelectedTableRow: () => Row<RowData> | undefined;
  getSelectedDevice: () => TTreeDevice | null;
  setData: (devices: TTreeDevice[]) => void;
  setExpandedRows: React.Dispatch<React.SetStateAction<ExpandedState>>;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedParentPath, setSelectedParentPath] = useState<string>(ROOT_PATH);
  const { t } = useTranslation();
  const schema = useMemo(() => createDeviceSchemaWithSnCheck(collectDeviceSns(data)), [data]);
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: createDefaultDevice(deviceInfo),
  });

  const selectedRow = getSelectedTableRow();
  const currentRowPath = selectedRow?.id;

  const currentParentPath = useMemo(() => {
    if (!currentRowPath) return ROOT_PATH;
    return getParentPath(currentRowPath) ?? ROOT_PATH;
  }, [currentRowPath]);

  const parentOptions = useMemo(() => {
    if (!currentRowPath) return [];
    return flattenDevicesWithPath(data, parseRowPath(currentRowPath));
  }, [data, currentRowPath]);

  const handleDeviceDuplicate = (formData: TDevice) => {
    if (!selectedRow || !currentRowPath) return;

    const sourcePath = parseRowPath(currentRowPath);
    const originalDevice = getDeviceAtPath(data, sourcePath);
    const existingSns = collectDeviceSns(data);
    existingSns.add(formData.sn);
    const newSubDevices = cloneSubDevicesWithNewIdentifiers(
      originalDevice?.subDevices,
      existingSns
    );
    const newDevice: TTreeDevice = { ...formData, subDevices: newSubDevices };
    const parentChanged = selectedParentPath !== currentParentPath;

    if (!parentChanged) {
      const newData = insertSiblingAfterPath(data, sourcePath, newDevice);
      setData(newData);

      const insertedIndex = sourcePath[sourcePath.length - 1];
      const parentKey = sourcePath.slice(0, -1).join('.');

      setExpandedRows((prev) => {
        if (prev === true) return true;
        const shifted = shiftExpandedKeys(prev, parentKey, insertedIndex, +1);
        if (sourcePath.length > 1) {
          shifted[parentKey] = true;
        }
        return shifted;
      });
    } else if (selectedParentPath === ROOT_PATH) {
      setData([...data, newDevice]);
    } else {
      const targetPath = parseRowPath(selectedParentPath);
      const newData = appendChildAtPath(data, targetPath, newDevice);
      setData(newData);

      setExpandedRows((prev) => {
        if (prev === true) return true;
        return { ...prev, [selectedParentPath]: true };
      });
    }

    setSelectedRows({});
    setIsDialogOpen(false);
  };

  const selectedDevice = getSelectedDevice();

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (open && selectedDevice) {
      const suffixes = buildSuffixes('clone');
      form.reset({
        ...selectedDevice,
        deviceId: '',
        connectedAt: '',
        updatedAt: '',
        createdAt: '',
        name: `${selectedDevice.name}${suffixes.name}`,
        sn: `${selectedDevice.sn}${suffixes.sn}`,
      });
      setSelectedParentPath(currentParentPath);
      setActiveTab('details');
    }
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={handleOpenChange}>
      <CredenzaTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex items-center gap-2 rounded-none px-2 py-5 font-normal hover:bg-gray-200 sm:px-3"
          disabled={!hasSelectedRow}
        >
          <CopyIcon className="text-muted-foreground size-4" />
          <span>{t('common.buttons.duplicateButton')}</span>
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        <Form {...form}>
          <CredenzaHeader>
            <CredenzaTitle>{t('devices.actions.duplicateDevice')}</CredenzaTitle>
          </CredenzaHeader>
          <CredenzaBody>
            <CredenzaDescription className="hidden">
              {t('devices.actions.duplicateDevice')}
            </CredenzaDescription>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">
                  {t('devices.actions.detailsTab')}
                </TabsTrigger>
                <TabsTrigger value="location" className="flex-1">
                  {t('devices.actions.locationTab')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <DeviceEditDetailsTabForm />
              </TabsContent>

              <TabsContent value="location">
                <DeviceEditMoveTabForm
                  parentOptions={parentOptions}
                  selectedParentPath={selectedParentPath}
                  onParentPathChange={setSelectedParentPath}
                />
              </TabsContent>
            </Tabs>
          </CredenzaBody>
          <CredenzaFooter>
            <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
              {t('common.buttons.cancelButton')}
            </Button>
            <Button
              type="button"
              onClick={form.handleSubmit(handleDeviceDuplicate, () => setActiveTab('details'))}
            >
              {t('common.buttons.saveChangesButton')}
            </Button>
          </CredenzaFooter>
        </Form>
      </CredenzaContent>
    </Credenza>
  );
};
